import {
	Box,
	Button,
	CircularProgress,
	FormControl,
	InputLabel,
	ListSubheader,
	MenuItem,
	Modal,
	OutlinedInput,
	Select,
	SelectChangeEvent,
	TextField,
	Typography,
} from "@mui/material";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useBus, useListener } from "react-bus";
import { AddDeviceModalProps } from "./types";
import { AppContext, IAppContext } from "../../../../Contexts/AppContext";
import { debounce } from "lodash";
import { CreateDeviceDto, DeviceStatus } from "../../../../models/Device";
import DeviceApi from "../../../../api/device";
import { isAxiosError } from "axios";

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import moment from "moment-timezone";
import { DatesUtils } from "../../../../utils/DatesUtils";

dayjs.extend(utc);
dayjs.extend(timezone);

const style = {
	position: "absolute" as "absolute",
	top: "50%",
	left: "50%",
	transform: "translate(-50%, -50%)",
	width: "90vh",
	bgcolor: "background.paper",
	border: "2px solid #000",
	boxShadow: 24,
	height: "90vh",
	overflowY: "scroll" as "scroll",
	p: 4,
};

export default function AddDeviceModal(props: AddDeviceModalProps) {
	const [open, setOpen] = useState(false);
	const [timezoneSearch, setTimezoneSearch] = useState("");
	const [device, setDevice] = useState<CreateDeviceDto>({
		deviceId: "", //RPU
		deviceType: "", //Model

		address: "",

		connectionType: "",

		socketId: "",

		permissionaryId: "",

		city: "",

		state: "",

		ipAddress: "",

		port: 0,

		lastLoadedProfileDate: "",

		lastLoadedProfileDateUtc: "",

		customerId: "",

		isBackUp: false,

		timeZone: "", //(UTC-06:00) Guadalajara, Mexico City, Monterrey

		accountNumber: "",

		commAddress: 0,

		status: DeviceStatus.OFFLINE,

		notes: "", //any notes about the device
	});
	const [errors, setErrors] = useState({
		deviceId: "",
	});
	const [loading, setLoading] = useState(false);
	const handleOpen = useCallback(() => setOpen(true), []);
	const handleClose = () => setOpen(false);
	const bus = useBus();

	const { userContext, setSnackBar, snackBar } = useContext(
		AppContext,
	) as IAppContext;

	const create = useCallback(async () => {
		setLoading(true);

		//lastLoadedProfileDate -> '2024-08-05 13:10:00'
		//lastLoadedProfileDateUtc -> '2024-08-05 13:10:00Z'

		const lastLoadedProfileDate = DatesUtils.formatDate(new Date());
		const createDeviceDto: CreateDeviceDto = {
			...device,
			permissionaryId: userContext?.permissionaryId || "",
			lastLoadedProfileDate: lastLoadedProfileDate,
			lastLoadedProfileDateUtc: DatesUtils.convertFromTimezoneToUtc(
				lastLoadedProfileDate,
				device.timeZone,
			),
		};

		try {
			await DeviceApi.createDevice({
				token: localStorage.getItem("accessToken"),
				device: createDeviceDto,
			});
			bus.emit("reloadDevicesScreenData");
			setSnackBar({
				open: true,
				message: "Dispositivo creado",
				autoHideDuration: 6000,
				severity: "success",
				onClose: (event: React.SyntheticEvent | Event) => {
					setSnackBar({
						...snackBar,
						open: false,
					});
				},
			});
			// handleClose();
		} catch (error: any) {
			console.error(error);
			let errorMsg = "Error al crear el dispositivo";
			if (isAxiosError(error)) {
				if (error.response?.status === 400) {
					errorMsg = error.response.data.message;
				}
			}
			setSnackBar({
				open: true,
				message: errorMsg,
				autoHideDuration: 6000,
				severity: "error",
				onClose: (event: React.SyntheticEvent | Event) => {
					setSnackBar({
						...snackBar,
						open: false,
					});
				},
			});
		} finally {
			setLoading(false);
		}
	}, [device, userContext]);

	const debouncedAction = useCallback(
		debounce((value) => {
			// Your action here
			fetchDuplicatedDeviceId();
		}, 500),
		[device.deviceId],
	); // Adjust the delay as needed

	const fetchDuplicatedDeviceId = useCallback(async () => {
		try {
			await DeviceApi.getOneDevice({
				token: localStorage.getItem("accessToken"),
				query: {
					deviceId: device.deviceId,
					permissionaryId: userContext?.permissionaryId,
				},
				includeExceptions: false,
			});

			setErrors({ ...errors, deviceId: "El identificador ya existe" });
		} catch (error: any) {
			if (isAxiosError(error)) {
				if (error.response?.status === 404) {
					setErrors({ ...errors, deviceId: "" });
				}
			}

			console.error(error);
		}
	}, [device.deviceId]);

	const timezones = useMemo(() => {
		return moment.tz.names().map((zone) => {
			const offset = moment.tz(zone).format("Z");
			return { name: zone, offset };
		});
	}, []);

	const filteredTimezones = useMemo(() => {
		if (!timezoneSearch.trim()) {
			return timezones;
		}
		const search = timezoneSearch.trim().toLowerCase();
		return timezones.filter(
			(tz) =>
				tz.name.toLowerCase().includes(search) ||
				tz.offset.toLowerCase().includes(search),
		);
	}, [timezones, timezoneSearch]);

	const handleTimezoneChange = (event: SelectChangeEvent) => {
		const value = event.target.value;
		setDevice((prevDevice) => ({ ...prevDevice, timeZone: value }));
	};

	const setProp = useCallback(
		(prop: string, value: string) => {
			setDevice({ ...device, [prop]: value });
		},
		[device],
	);

	useEffect(() => {
		debouncedAction(device.deviceId);
	}, [device.deviceId]);

	useListener("openAddDeviceModal", handleOpen);
	return (
		<Modal
			open={open}
			onClose={handleClose}
			aria-labelledby='modal-modal-title'
			aria-describedby='modal-modal-description'>
			<Box sx={style}>
				<Typography
					textAlign={"center"}
					id='modal-modal-title'
					variant='h6'
					component='h2'>
					Agregar Medidor
				</Typography>
				<div
					style={{ marginBottom: "15px", width: "100%", textAlign: "center" }}>
					<FormControl sx={{ m: 1, width: "90%" }} variant='outlined'>
						<InputLabel htmlFor='outlined-adornment-device-id'>
							Device ID
						</InputLabel>
						<OutlinedInput
							id='outlined-adornment-device-id'
							type={"text"}
							placeholder='Identificador'
							label='Identificador'
							error={errors.deviceId ? true : false}
							aria-errormessage='Error'
							onChange={(event) => {
								setProp("deviceId", event.target.value);
							}}
							onKeyUp={(event) => {
								if (event.key === "Enter") {
									// create();
								}
							}}
						/>
						{errors.deviceId && (
							<Typography
								textAlign={"center"}
								id='modal-modal-title'
								variant='caption'
								sx={{ color: "red", marginTop: 1 }}
								component='p'>
								{errors.deviceId}
							</Typography>
						)}
					</FormControl>
				</div>
				{/*Agregar Modelo Device Type */}
				<div
					style={{ marginBottom: "15px", width: "100%", textAlign: "center" }}>
					<FormControl sx={{ m: 1, width: "90%" }} variant='outlined'>
						<InputLabel htmlFor='outlined-adornment-model'>Modelo</InputLabel>
						<OutlinedInput
							id='outlined-adornment-model'
							type={"text"}
							placeholder='Modelo'
							label='Modelo'
							onChange={(event) => {
								setProp("deviceType", event.target.value);
							}}
							onKeyUp={(event) => {
								if (event.key === "Enter") {
									// create();
								}
							}}
						/>
					</FormControl>
				</div>

				{/*Agregar CustomeId */}
				<div
					style={{ marginBottom: "15px", width: "100%", textAlign: "center" }}>
					<FormControl sx={{ m: 1, width: "90%" }} variant='outlined'>
						<InputLabel htmlFor='outlined-adornment-model'>Cliente</InputLabel>
						<OutlinedInput
							id='outlined-adornment-model'
							type={"text"}
							placeholder='Cliente'
							label='Cliente'
							onChange={(event) => {
								setProp("customerId", event.target.value);
							}}
							onKeyUp={(event) => {
								if (event.key === "Enter") {
									// create();
								}
							}}
						/>
					</FormControl>
				</div>

				{/*Agregar Ciudad */}
				<div
					style={{ marginBottom: "15px", width: "100%", textAlign: "center" }}>
					<FormControl sx={{ m: 1, width: "90%" }} variant='outlined'>
						<InputLabel htmlFor='outlined-adornment-city'>Ciudad</InputLabel>
						<OutlinedInput
							id='outlined-adornment-city'
							type={"text"}
							placeholder='Ciudad'
							label='Ciudad'
							onChange={(event) => {
								setProp("city", event.target.value);
							}}
							onKeyUp={(event) => {
								if (event.key === "Enter") {
									// create();
								}
							}}
						/>
					</FormControl>
				</div>

				{/*Agregar Estado */}
				<div
					style={{ marginBottom: "15px", width: "100%", textAlign: "center" }}>
					<FormControl sx={{ m: 1, width: "90%" }} variant='outlined'>
						<InputLabel htmlFor='outlined-adornment-state'>Estado</InputLabel>
						<OutlinedInput
							id='outlined-adornment-state'
							type={"text"}
							placeholder='Estado'
							label='Estado'
							onChange={(event) => {
								setProp("state", event.target.value);
							}}
							onKeyUp={(event) => {
								if (event.key === "Enter") {
									// create();
								}
							}}
						/>
					</FormControl>
				</div>
				{/*Agregar Zona horaria dropdown */}
				<div
					style={{ marginBottom: "15px", width: "100%", textAlign: "center" }}>
					<FormControl sx={{ m: 1, width: "90%" }} variant='outlined'>
						<InputLabel htmlFor='outlined-adornment-timezone'>
							Time Zone
						</InputLabel>
						<Select
							id='outlined-adornment-timezone'
							value={device.timeZone}
							onChange={handleTimezoneChange}
							label='Time Zone'
							onOpen={() => setTimezoneSearch("")}
							MenuProps={{
								PaperProps: {
									style: { maxHeight: 360 },
								},
							}}>
							<ListSubheader>
								<TextField
									autoFocus
									size='small'
									placeholder='Search time zone'
									fullWidth
									value={timezoneSearch}
									onChange={(event) => setTimezoneSearch(event.target.value)}
									onKeyDown={(event) => event.stopPropagation()}
								/>
							</ListSubheader>
							{filteredTimezones.map((tz) => (
								<MenuItem key={tz.name} value={`${tz.name} (UTC${tz.offset})`}>
									{`${tz.name} (UTC${tz.offset})`}
								</MenuItem>
							))}
						</Select>
					</FormControl>
				</div>

				{/*Agregar Tipo de Conexion */}
				<div
					style={{ marginBottom: "15px", width: "100%", textAlign: "center" }}>
					<FormControl sx={{ m: 1, width: "90%" }}>
						<InputLabel id='select-connection-type'>
							Tipo de Conexion
						</InputLabel>
						<Select
							labelId='select-connection-type'
							id='select-connection-type'
							value={device.connectionType}
							label='Tipo de Conexion'
							onChange={(event: SelectChangeEvent) => {
								setProp("connectionType", event.target.value as string);
							}}>
							<MenuItem value='TCP/Ethernet'>TCP/Ethernet</MenuItem>
							<MenuItem value='GPRS'>GPRS</MenuItem>
							<MenuItem value='WIFI'>WIFI</MenuItem>
						</Select>
					</FormControl>
				</div>

				{/*Agregar Puerto */}
				<div
					style={{ marginBottom: "15px", width: "100%", textAlign: "center" }}>
					<FormControl sx={{ m: 1, width: "90%" }}>
						<InputLabel htmlFor='outlined-adornment-port'>Puerto</InputLabel>
						<OutlinedInput
							id='outlined-adornment-port'
							type={"text"}
							placeholder='Puerto'
							label='Puerto'
							onChange={(event) => {
								setProp("port", event.target.value);
							}}
							onKeyUp={(event) => {
								if (event.key === "Enter") {
									// create();
								}
							}}
						/>
					</FormControl>
				</div>
				<div
					style={{ marginBottom: "15px", width: "100%", textAlign: "center" }}>
					<FormControl sx={{ m: 1, width: "90%" }} variant='outlined'>
						<InputLabel htmlFor='outlined-adornment-username'>
							Direccion IP
						</InputLabel>
						<OutlinedInput
							id='outlined-adornment-username'
							type={"text"}
							placeholder='Direccion IP'
							label='Direccion IP'
							onChange={(event) => {
								setProp("ipAddress", event.target.value);
							}}
							onKeyUp={(event) => {
								if (event.key === "Enter") {
									// create();
								}
							}}
						/>
					</FormControl>
				</div>
				<div
					style={{ marginBottom: "15px", width: "100%", textAlign: "center" }}>
					<FormControl sx={{ m: 1, width: "90%" }} variant='outlined'>
						<InputLabel htmlFor='outlined-adornment-username'>
							Descripcion
						</InputLabel>
						<OutlinedInput
							id='outlined-adornment-username'
							type={"text"}
							placeholder='Descripcion'
							label='Descripcion'
							multiline
							onChange={(event) => {
								setProp("notes", event.target.value);
							}}
							onKeyUp={(event) => {
								if (event.key === "Enter") {
									// create();
								}
							}}
						/>
					</FormControl>
				</div>
				<div
					style={{ marginBottom: "15px", width: "100%", textAlign: "center" }}>
					<FormControl sx={{ m: 1, width: "90%" }} variant='outlined'>
						<InputLabel htmlFor='outlined-adornment-address'>
							Direccion del medidor
						</InputLabel>
						<OutlinedInput
							id='outlined-adornment-address'
							type={"text"}
							placeholder='Direccion'
							multiline
							label='Direccion del medidor'
							onChange={(event) => {
								setProp("address", event.target.value);
							}}
							onKeyUp={(event) => {
								if (event.key === "Enter") {
									// create();
								}
							}}
						/>
					</FormControl>
				</div>
				<div style={{ marginTop: "10px", textAlign: "center" }}>
					{loading ? (
						<CircularProgress size={30} />
					) : (
						<Button
							// disabled={user.email === "" || user.password === ""}
							variant='contained'
							color='primary'
							sx={{
								width: "40%",
								marginTop: 2,
							}}
							onClick={() => create()}>
							Crear
						</Button>
					)}
				</div>
			</Box>
		</Modal>
	);
}
